import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { jobReportsKey, loadReportsConfigForJob } from '../../api/reports';
import {
  releaseReportsCopyTarget,
  reportsConfigQueryKeys,
  retainReportsCopyTarget,
  useReportsConfig,
  useReportsCopyPending,
  useSaveReportsConfig,
} from '../../hooks/useReportsConfig';
import { getReportsLoadErrorMessage, getReportsSaveErrorMessage } from '../../utils/reportsConfigErrors';
import {
  validateReportsConfig,
  formatValidationSummary,
} from '../../utils/validateReportsConfig';
import { useGetJobs } from '../../../reference/api/react-query';
import DesignerCanvas from './DesignerCanvas';
import DesignerSidePanel from './DesignerSidePanel';
import { LoadingBlock, EmptyState } from '../../../../shared/ui/composites';
import { genId } from './constants';
import { cloneReport, cloneWidget, nextLayoutItem } from '../../utils/cloneReport';

function emptyReport(name = 'گزارش جدید') {
  return {
    id: genId(),
    name,
    is_main_dashboard: false,
    visible: true,
    order: 0,
    queries: [],
    tabs: [{
      id: genId(),
      label: 'تب ۱',
      visible: true,
      order: 0,
      widgets: [],
      layout: [],
    }],
  };
}

export default function ReportDesignerPage() {
  const { data: jobs = [] } = useGetJobs();
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [reports, setReports] = useState([]);
  const [showCustomerTarget, setShowCustomerTarget] = useState(false);
  const [defaultReportId, setDefaultReportId] = useState(null);
  const [useCustomAsMainDashboard, setUseCustomAsMainDashboard] = useState(false);
  const [mainDashboardReportId, setMainDashboardReportId] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedTabId, setSelectedTabId] = useState(null);
  const [selectedWidgetId, setSelectedWidgetId] = useState(null);
  const [sideTab, setSideTab] = useState('reports');
  const [isDirty, setIsDirty] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [hydratedJobId, setHydratedJobId] = useState(null);
  const queryClient = useQueryClient();
  const saveReportsConfig = useSaveReportsConfig();

  const selectedJob = useMemo(
    () => (jobs || []).find((j) => Number(j.id) === Number(selectedJobId)) || null,
    [jobs, selectedJobId],
  );
  const maxLevel = Math.min(
    4,
    Math.max(1, Number(selectedJob?.level_type) || 4),
  );

  useEffect(() => {
    if (selectedJobId != null) return;
    if (!jobs?.length) return;
    const admin = jobs.find((j) => Number(j.id) === 77);
    setSelectedJobId(Number(admin?.id ?? jobs[0].id));
  }, [jobs, selectedJobId]);

  const {
    data: configData,
    isLoading: configLoading,
    isPending: configPending,
    isFetching: configFetching,
    isFetchedAfterMount: configFetchedAfterMount,
    isError: configError,
    error: configQueryError,
    refetch: refetchConfig,
  } = useReportsConfig({
    jobId: selectedJobId,
    levelType: selectedJob?.level_type ?? selectedJobId,
    enabled: selectedJobId != null,
  });
  const selectedRoleKey = selectedJobId != null ? jobReportsKey(selectedJobId) : null;
  const pendingCopyCount = useReportsCopyPending(queryClient, selectedRoleKey);
  const configInvalidated = Boolean(
    selectedRoleKey
    && queryClient.getQueryState(reportsConfigQueryKeys.byRole(selectedRoleKey))?.isInvalidated,
  );

  const configReady = hydratedJobId === selectedJobId && !configError;
  const showConfigLoading = selectedJobId != null && !configError && !configReady;

  const hydrateFromConfig = useCallback((parsed) => {
    const arr = parsed.reports.map((r, i) => ({
      visible: true,
      order: i,
      ...r,
      tabs: (r.tabs || []).map((t, j) => ({ visible: true, order: j, ...t })),
    }));
    setReports(arr);
    setDefaultReportId(parsed.defaultReportId || arr[0]?.id || null);
    setUseCustomAsMainDashboard(parsed.useCustomAsMainDashboard !== false);
    setMainDashboardReportId(parsed.mainDashboardReportId || arr[0]?.id || null);
    if (arr.length > 0) {
      setSelectedReportId(arr[0].id);
      setSelectedTabId(arr[0].tabs?.[0]?.id);
    } else {
      setSelectedReportId(null);
      setSelectedTabId(null);
    }
    setSelectedWidgetId(null);
    setSideTab('reports');
    setIsDirty(false);
  }, []);

  const resetEmptyConfig = useCallback(() => {
    setReports([]);
    setShowCustomerTarget(false);
    setDefaultReportId(null);
    setUseCustomAsMainDashboard(true);
    setMainDashboardReportId(null);
    setSelectedReportId(null);
    setSelectedTabId(null);
    setSelectedWidgetId(null);
    setSideTab('reports');
    setIsDirty(false);
  }, []);

  useEffect(() => {
    if (selectedJobId == null) return;
    setHydratedJobId(null);
    setSelectedReportId(null);
    setSelectedTabId(null);
    setSelectedWidgetId(null);
    setSideTab('reports');
    setIsDirty(false);
  }, [selectedJobId]);

  useEffect(() => {
    if (selectedJobId == null || configLoading || configPending) return;
    if (configError) return;
    // A copy onto this job can still be in flight, including after this page
    // unmounts and mounts again. Its cache is not invalidated until the save
    // returns, so hydrating now would keep the pre-copy snapshot.
    if (pendingCopyCount > 0) return;
    // An invalidated entry still holds the pre-copy snapshot until this visit's
    // refetch settles. Hydrating it lets a later save overwrite the copy.
    if (configInvalidated && (configFetching || !configFetchedAfterMount)) return;
    if (hydratedJobId === selectedJobId) return;

    if (!configData?.parsed) {
      resetEmptyConfig();
      setHydratedJobId(selectedJobId);
      return;
    }

    hydrateFromConfig(configData.parsed);
    setHydratedJobId(selectedJobId);
  }, [
    selectedJobId,
    selectedRoleKey,
    pendingCopyCount,
    configLoading,
    configPending,
    configFetching,
    configFetchedAfterMount,
    configInvalidated,
    configError,
    configData,
    hydratedJobId,
    hydrateFromConfig,
    resetEmptyConfig,
  ]);

  const handleJobChange = useCallback((nextJobId) => {
    const next = Number(nextJobId);
    if (next === Number(selectedJobId)) return;
    if (isDirty) {
      const confirmed = window.confirm(
        'تغییرات ذخیره نشده دارید. آیا مطمئن هستید که می‌خواهید نقش را تغییر دهید؟',
      );
      if (!confirmed) return;
    }
    setSaveError(null);
    setValidationErrors([]);
    setSelectedJobId(next);
  }, [selectedJobId, isDirty]);

  const markDirty = () => {
    setIsDirty(true);
    setValidationErrors([]);
    setSaveError(null);
  };

  // ── report ops ─────────────────────────────────────────────────────────────
  const handleAddReport = () => {
    const r = emptyReport(`گزارش ${reports.length + 1}`);
    r.order = reports.length;
    setReports((prev) => [...prev, r]);
    setSelectedReportId(r.id);
    setSelectedTabId(r.tabs[0].id);
    setSelectedWidgetId(null);
    setSideTab('reports');
    markDirty();
  };

  const handleDeleteReport = (id) => {
    if (!window.confirm('آیا مطمئن هستید؟')) return;
    setReports((prev) => {
      const next = prev.filter((r) => r.id !== id);
      if (selectedReportId === id) {
        setSelectedReportId(next[0]?.id || null);
        setSelectedTabId(next[0]?.tabs?.[0]?.id || null);
        setSelectedWidgetId(null);
        setSideTab('reports');
      }
      return next;
    });
    if (mainDashboardReportId === id) {
      setMainDashboardReportId(null);
    }
    if (defaultReportId === id) {
      setDefaultReportId(reports.filter((r) => r.id !== id)[0]?.id || null);
    }
    markDirty();
  };

  const handleRenameReport = (id, name) => {
    setReports((prev) => prev.map((r) => r.id === id ? { ...r, name } : r));
    markDirty();
  };

  const handleReorderReports = (newOrder) => {
    setReports(newOrder);
    markDirty();
  };

  const handleToggleReportVisible = (id) => {
    setReports((prev) => prev.map((r) => r.id === id ? { ...r, visible: r.visible === false ? true : false } : r));
    markDirty();
  };

  const handleToggleMainDashboard = (id, checked) => {
    setReports((prev) =>
      prev.map((r) => r.id === id
        ? { ...r, is_main_dashboard: checked }
        : checked ? { ...r, is_main_dashboard: false } : r,
      ),
    );
    if (checked) {
      setMainDashboardReportId(id);
      setUseCustomAsMainDashboard(true);
    }
    markDirty();
  };

  const handleShowCustomerTarget = (checked) => {
    setShowCustomerTarget(checked);
    markDirty();
  };

  const handleDefaultReportChange = (id) => {
    setDefaultReportId(id);
    markDirty();
  };

  const handleUseCustomAsMainDashboard = (checked) => {
    setUseCustomAsMainDashboard(checked);
    if (checked) {
      const nextId = mainDashboardReportId || reports[0]?.id || null;
      setMainDashboardReportId(nextId);
      if (nextId) {
        setReports((prev) =>
          prev.map((r) => ({ ...r, is_main_dashboard: r.id === nextId })),
        );
      }
    } else {
      setReports((prev) => prev.map((r) => ({ ...r, is_main_dashboard: false })));
    }
    markDirty();
  };

  const handleMainDashboardReportChange = (id) => {
    setMainDashboardReportId(id);
    setUseCustomAsMainDashboard(true);
    setReports((prev) =>
      prev.map((r) => ({ ...r, is_main_dashboard: r.id === id })),
    );
    markDirty();
  };

  const handleSelectReport = (id) => {
    setSelectedReportId(id);
    const r = reports.find((r) => r.id === id);
    setSelectedTabId(r?.tabs?.[0]?.id || null);
    setSelectedWidgetId(null);
    setSideTab('reports');
  };

  const handleReportQueriesChange = (queries) => {
    if (!selectedReportId) return;
    updateReport(selectedReportId, (r) => ({ ...r, queries }));
    markDirty();
  };

  // ── tab ops ────────────────────────────────────────────────────────────────
  const updateReport = (id, fn) =>
    setReports((prev) => prev.map((r) => r.id === id ? fn(r) : r));

  const handleAddTab = () => {
    const tabId = genId();
    updateReport(selectedReportId, (r) => ({
      ...r,
      tabs: [...(r.tabs || []), {
        id: tabId,
        label: `تب ${(r.tabs?.length || 0) + 1}`,
        visible: true,
        order: (r.tabs?.length || 0),
        widgets: [],
        layout: [],
      }],
    }));
    setSelectedTabId(tabId);
    markDirty();
  };

  const handleRenameTab = (tabId, label) => {
    updateReport(selectedReportId, (r) => ({
      ...r,
      tabs: r.tabs.map((t) => t.id === tabId ? { ...t, label } : t),
    }));
    markDirty();
  };

  const handleDeleteTab = (tabId) => {
    updateReport(selectedReportId, (r) => {
      const next = r.tabs.filter((t) => t.id !== tabId);
      if (selectedTabId === tabId) setSelectedTabId(next[0]?.id || null);
      return { ...r, tabs: next };
    });
    setSelectedWidgetId(null);
    setSideTab('reports');
    markDirty();
  };

  const handleToggleTabVisible = (tabId) => {
    updateReport(selectedReportId, (r) => ({
      ...r,
      tabs: r.tabs.map((t) => t.id === tabId ? { ...t, visible: t.visible === false ? true : false } : t),
    }));
    markDirty();
  };

  const handleReorderTabs = (newTabs) => {
    updateReport(selectedReportId, (r) => ({ ...r, tabs: newTabs }));
    markDirty();
  };

  // ── layout ─────────────────────────────────────────────────────────────────
  const handleLayoutChange = (tabId, newLayout) => {
    const clamped = (newLayout || []).map((item) => {
      const w = Math.max(2, Math.min(Number(item.w) || 2, 12));
      const h = Math.max(2, Number(item.h) || 2);
      const x = Math.max(0, Math.min(Number(item.x) || 0, 12 - w));
      const y = Math.max(0, Number(item.y) || 0);
      return { ...item, x, y, w, h, minW: 2, minH: 2, maxW: 12 };
    });

    const current = reports.find((r) => r.id === selectedReportId)
      ?.tabs?.find((t) => t.id === tabId)?.layout || [];
    const same =
      current.length === clamped.length &&
      clamped.every((item) => {
        const prev = current.find((p) => p.i === item.i);
        return prev && prev.x === item.x && prev.y === item.y && prev.w === item.w && prev.h === item.h;
      });
    if (same) return;

    updateReport(selectedReportId, (r) => ({
      ...r,
      tabs: r.tabs.map((t) => t.id === tabId ? { ...t, layout: clamped } : t),
    }));
    markDirty();
  };

  // ── widget ops ─────────────────────────────────────────────────────────────
  const handleAddWidget = (tabId, widget, layoutItem) => {
    updateReport(selectedReportId, (r) => ({
      ...r,
      tabs: r.tabs.map((t) =>
        t.id === tabId
          ? { ...t, widgets: [...(t.widgets || []), widget], layout: [...(t.layout || []), layoutItem] }
          : t,
      ),
    }));
    setSelectedWidgetId(widget.id);
    setSideTab('widget');
    markDirty();
  };

  const handleSelectWidget = (id) => {
    setSelectedWidgetId(id);
    setSideTab('widget');
  };

  const handleDeselectWidget = () => {
    setSelectedWidgetId(null);
    setSideTab('reports');
  };

  const handleWidgetChange = (updatedWidget) => {
    updateReport(selectedReportId, (r) => ({
      ...r,
      tabs: r.tabs.map((t) =>
        t.id === selectedTabId
          ? { ...t, widgets: t.widgets.map((w) => w.id === updatedWidget.id ? updatedWidget : w) }
          : t,
      ),
    }));
    markDirty();
  };

  const handleDuplicateWidget = (tabId, widgetId) => {
    let copyId = null;
    updateReport(selectedReportId, (r) => ({
      ...r,
      tabs: r.tabs.map((t) => {
        if (t.id !== tabId) return t;
        const source = t.widgets.find((w) => w.id === widgetId);
        const sourceLay = t.layout.find((l) => l.i === widgetId);
        if (!source) return t;
        const copy = cloneWidget(source);
        copyId = copy.id;
        const layoutItem = nextLayoutItem(t.layout, { ...sourceLay, i: copy.id });
        return {
          ...t,
          widgets: [...t.widgets, copy],
          layout: [...t.layout, layoutItem],
        };
      }),
    }));
    if (copyId) {
      setSelectedWidgetId(copyId);
      setSideTab('widget');
    }
    markDirty();
  };

  const handleCopyWidget = (sourceTabId, widgetId, targetTabId) => {
    const report = reports.find((r) => r.id === selectedReportId);
    const sourceTab = report?.tabs?.find((t) => t.id === sourceTabId);
    const source = sourceTab?.widgets?.find((w) => w.id === widgetId);
    const sourceLay = sourceTab?.layout?.find((l) => l.i === widgetId);
    if (!source) return;
    const copy = cloneWidget(source);
    updateReport(selectedReportId, (r) => ({
      ...r,
      tabs: r.tabs.map((t) => {
        if (t.id !== targetTabId) return t;
        const layoutItem = nextLayoutItem(t.layout, { ...sourceLay, i: copy.id });
        return {
          ...t,
          widgets: [...(t.widgets || []), copy],
          layout: [...(t.layout || []), layoutItem],
        };
      }),
    }));
    setSelectedTabId(targetTabId);
    setSelectedWidgetId(copy.id);
    setSideTab('widget');
    markDirty();
  };

  const handleDuplicateReport = (id) => {
    const source = reports.find((r) => r.id === id);
    if (!source) return;
    const copy = cloneReport(source);
    copy.order = reports.length;
    copy.visible = true;
    setReports((prev) => [...prev, copy]);
    setSelectedReportId(copy.id);
    setSelectedTabId(copy.tabs?.[0]?.id || null);
    setSelectedWidgetId(null);
    setSideTab('reports');
    markDirty();
  };

  const handleCopyReportToJob = async (id, targetJobId) => {
    if (hydratedJobId !== selectedJobId) return;
    const source = reports.find((r) => r.id === id);
    const targetJob = (jobs || []).find((j) => Number(j.id) === Number(targetJobId));
    if (!source || !targetJob) return;
    if (Number(targetJobId) === Number(selectedJobId)) {
      handleDuplicateReport(id);
      return;
    }
    const targetRoleKey = jobReportsKey(targetJobId);
    retainReportsCopyTarget(queryClient, targetRoleKey);
    setSaveError(null);
    setValidationErrors([]);
    try {
      const loaded = await loadReportsConfigForJob(targetJobId, targetJob.level_type);
      const parsed = loaded.parsed || { reports: [] };
      const copy = cloneReport(source);
      copy.order = (parsed.reports || []).length;
      copy.visible = true;
      copy.is_main_dashboard = false;
      const config = {
        ...parsed,
        reports: [...(parsed.reports || []), copy],
      };
      const validation = validateReportsConfig(config);
      if (!validation.valid) {
        const summary = formatValidationSummary(validation.errors);
        setSaveError(summary);
        window.alert(summary);
        return;
      }
      await saveReportsConfig.mutateAsync({
        roleKey: targetRoleKey,
        config,
      });
      window.alert(`گزارش «${copy.name}» روی نقش «${targetJob.name || targetJobId}» کپی شد.`);
    } catch (err) {
      window.alert(err?.response?.data?.message || err.message || 'کپی گزارش ناموفق بود');
    } finally {
      releaseReportsCopyTarget(queryClient, targetRoleKey);
    }
  };

  const handleDeleteWidget = (tabId, widgetId) => {
    updateReport(selectedReportId, (r) => ({
      ...r,
      tabs: r.tabs.map((t) =>
        t.id === tabId
          ? {
              ...t,
              widgets: t.widgets.filter((w) => w.id !== widgetId),
              layout: t.layout.filter((l) => l.i !== widgetId),
            }
          : t,
      ),
    }));
    if (selectedWidgetId === widgetId) {
      setSelectedWidgetId(null);
      setSideTab('reports');
    }
    markDirty();
  };

  const handleLayoutItemChange = (patch) => {
    if (!selectedWidgetId) return;
    updateReport(selectedReportId, (r) => ({
      ...r,
      tabs: r.tabs.map((t) => {
        if (t.id !== selectedTabId) return t;
        const layout = t.layout.map((l) => {
          if (l.i !== selectedWidgetId) return l;
          const next = { ...l, ...patch };
          const w = Math.max(2, Math.min(Number(next.w) || 2, 12));
          const h = Math.max(2, Number(next.h) || 2);
          const x = Math.max(0, Math.min(Number(next.x) || 0, 12 - w));
          const y = Math.max(0, Number(next.y) || 0);
          return { ...next, x, y, w, h, minW: 2, minH: 2, maxW: 12 };
        });
        return { ...t, layout };
      }),
    }));
    markDirty();
  };

  // ── save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (selectedJobId == null || hydratedJobId !== selectedJobId) return;
    setSaveError(null);

    const config = {
      showCustomerTarget: false,
      defaultReportId,
      useCustomAsMainDashboard,
      mainDashboardReportId,
      reports,
    };
    const validation = validateReportsConfig(config);
    if (!validation.valid) {
      setValidationErrors(validation.errors);
      setSaveError(formatValidationSummary(validation.errors));

      const first = validation.errors.find((e) => e.widgetId);
      if (first?.reportId) {
        setSelectedReportId(first.reportId);
        if (first.tabId) setSelectedTabId(first.tabId);
        setSelectedWidgetId(first.widgetId);
        setSideTab('widget');
      }
      return;
    }

    setValidationErrors([]);
    try {
      await saveReportsConfig.mutateAsync({
        roleKey: jobReportsKey(selectedJobId),
        config,
      });
      setIsDirty(false);
      setSaveError(null);
      setValidationErrors([]);
    } catch (err) {
      setSaveError(getReportsSaveErrorMessage(err));
    }
  };

  // ── derived ────────────────────────────────────────────────────────────────
  const selectedReport = reports.find((r) => r.id === selectedReportId) || null;
  const currentTab = selectedReport?.tabs?.find((t) => t.id === selectedTabId);
  const selectedWidget = currentTab?.widgets?.find((w) => w.id === selectedWidgetId) || null;
  const selectedLayout = currentTab?.layout?.find((l) => l.i === selectedWidgetId) || null;
  const configLoadErrorMessage = configError
    ? getReportsLoadErrorMessage(configQueryError)
    : null;

  return (
    /*
      Fill the SidebarInset viewport and lock height so ONLY the canvas
      scroll area moves — the tabbed dock + «افزودن ویجت» bar stay pinned.
    */
    <div className="relative h-[100svh] max-h-[100svh] w-full min-w-0 overflow-hidden bg-slate-100 max-lg:h-[calc(100svh-var(--mobile-topbar-height,3.5rem))] max-lg:max-h-[calc(100svh-var(--mobile-topbar-height,3.5rem))]">
      <div className="absolute inset-0 flex min-h-0 overflow-hidden" dir="ltr">
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {showConfigLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-100/90">
              <LoadingBlock message="در حال بارگذاری تنظیمات گزارش…" />
            </div>
          )}
          {configError && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-100 p-6">
              <EmptyState
                message={configLoadErrorMessage}
                actionLabel={configFetching ? 'در حال تلاش…' : 'تلاش مجدد'}
                onAction={() => refetchConfig()}
              />
            </div>
          )}
          <DesignerCanvas
            report={configReady ? selectedReport : null}
            maxLevel={maxLevel}
            selectedTabId={selectedTabId}
            onSelectTab={setSelectedTabId}
            onAddTab={handleAddTab}
            onRenameTab={handleRenameTab}
            onDeleteTab={handleDeleteTab}
            onToggleTabVisible={handleToggleTabVisible}
            onReorderTabs={handleReorderTabs}
            onLayoutChange={handleLayoutChange}
            onAddWidget={handleAddWidget}
            onDeleteWidget={handleDeleteWidget}
            onDuplicateWidget={handleDuplicateWidget}
            onCopyWidget={handleCopyWidget}
            selectedWidgetId={selectedWidgetId}
            onSelectWidget={handleSelectWidget}
            onDeselect={handleDeselectWidget}
          />
        </div>

        <DesignerSidePanel
          activeTab={sideTab}
          onTabChange={setSideTab}
          jobs={jobs}
          selectedJobId={selectedJobId}
          onJobChange={handleJobChange}
          reports={reports}
          selectedReportId={selectedReportId}
          onSelectReport={handleSelectReport}
          onAddReport={handleAddReport}
          onDeleteReport={handleDeleteReport}
          onDuplicateReport={handleDuplicateReport}
          onCopyReportToJob={handleCopyReportToJob}
          onRenameReport={handleRenameReport}
          onReorderReports={handleReorderReports}
          onToggleReportVisible={handleToggleReportVisible}
          onToggleMainDashboard={handleToggleMainDashboard}
          showCustomerTarget={showCustomerTarget}
          onShowCustomerTarget={handleShowCustomerTarget}
          defaultReportId={defaultReportId}
          onDefaultReportChange={handleDefaultReportChange}
          useCustomAsMainDashboard={useCustomAsMainDashboard}
          onUseCustomAsMainDashboard={handleUseCustomAsMainDashboard}
          mainDashboardReportId={mainDashboardReportId}
          onMainDashboardReportChange={handleMainDashboardReportChange}
          isDirty={hydratedJobId === selectedJobId && isDirty}
          onSave={handleSave}
          saving={saveReportsConfig.isPending}
          saveError={saveError}
          validationErrors={validationErrors}
          maxLevel={maxLevel}
          selectedWidget={selectedWidget}
          selectedLayout={selectedLayout}
          onChangeWidget={handleWidgetChange}
          onLayoutItemChange={handleLayoutItemChange}
          onCloseWidget={handleDeselectWidget}
          selectedReport={selectedReport}
          onChangeReportQueries={handleReportQueriesChange}
        />
      </div>
    </div>
  );
}
